/**
 * What the town says when nobody is fighting.
 *
 * ── Why this is content and not simulation ──────────────────────────────────
 *
 * `src/sim/systems/villagers.ts` already settled the hard half of this
 * question and its answer is quoted here because it decides this file too:
 * **a villager's position is state, and how it looks having got there is
 * decoration.** A man's errand is hashed, saved and replayed; the sentence over
 * his head is not. Nothing below feeds back, nothing accumulates, and turning
 * the bubbles off changes no outcome — which is the test for whether a thing
 * belongs in the renderer. So the *lines* live here, in `src/data`, where
 * content lives; the *choosing* lives in `src/render/chatter.ts`, and the
 * simulation never hears about either.
 *
 * This file imports nothing at all, which is the strongest form of the layering
 * rule available: `src/data` may import only `src/sim/types.ts`, and this needs
 * even less than that.
 *
 * ── Why the mood keys are strings and not `ErrandId` ────────────────────────
 *
 * `Errand` is declared in `src/sim/systems/villagers.ts`, and `src/data` may
 * not import from `src/sim/systems`. Mirroring the enum here would put a second
 * authority for the same six numbers in the codebase, which is the mistake
 * `data/faces.ts` documents at length. So the bank is keyed by a **word** that
 * describes a situation, the renderer maps its own `ErrandId` onto that word,
 * and there is exactly one place — `moodOf` in `render/chatter.ts` — where the
 * two vocabularies meet.
 *
 * `haul` is not an errand at all: it is what a villager says while his basket
 * is full, whatever errand he is nominally on. `moodOf` puts it first for the
 * same reason `folk.ts` draws the load first — the walk home is the part of the
 * trip worth watching.
 *
 * ── Two mouths, and what the difference is allowed to be ────────────────────
 *
 * The ask was male and female chat boxes, so a town has to sound like it holds
 * two kinds of people. The difference is *concern and register*, never trade:
 * women here work the seam and stand the levy and men here mind the pot and the
 * baby, because the alternative is a joke the game does not want to make. Most
 * lines are in `both` for exactly that reason, and the gendered banks exist to
 * make a street sound mixed rather than to sort it.
 */

/** A situation somebody might have something to say about. */
export type Mood = 'talk' | 'gather' | 'rest' | 'pray' | 'work' | 'haul';

/** Which mouth. Read the note above before adding a third. */
export const Mouth = { Man: 0, Woman: 1 } as const;
export type MouthId = (typeof Mouth)[keyof typeof Mouth];

export interface MoodLines {
  /** Said by anybody. Most lines belong here. */
  both?: readonly string[];
  men?: readonly string[];
  women?: readonly string[];
}

export interface ChatterBank {
  /**
   * This people says nothing, ever, and the silence is the characterisation.
   * Only the Litany, and `RHYTHMS` already agrees with it — their `talk` weight
   * is zero. This flag covers the moods where it is not.
   */
  silent?: boolean;
  moods: Partial<Record<Mood, MoodLines>>;
}

/**
 * What anybody in any settlement might say.
 *
 * The register is the world's: tired, dry, specific, and about the next hour
 * rather than about the war. A line that could be spoken by a hero is wrong
 * here — these are the people the hero is standing in front of.
 */
export const COMMON: ChatterBank = {
  moods: {
    talk: {
      both: [
        'Half the roof, and the half that leaks.',
        "He says the wall holds. He says a lot of things.",
        'Ash in the water butt again.',
        'Third night running I have heard them out past the ditch.',
        'Was a road there. My father walked it.',
        'They counted us twice and fed us once.',
        "Don't look at the sky. Nothing good learned that way.",
        'I would trade a week of bread for one quiet night.',
        'You hear the bell go early? That was not the hour.',
        'She was on the north gate. She is not on it now.',
        'Nothing wrong with the well. Something wrong with what is in it.',
        'They are counting the roofs again. That is never for our good.',
        'Cart came in light. Cart always comes in light.',
        'I stopped asking where it goes. Costs less that way.',
        'Two of the Ledger men, at the door, before the bell.',
        'Whatever is out past the ditch, it has learned the hours.',
        'Ground is wrong out east. Nothing takes.',
        'He came back. Only the once, mind.',
        'Wall is fine. It is the gate I would not stand under.',
        'Say what you like. Say it quieter.',
      ],
      men: [
        'The lad wants a spear. He is nine.',
        'My back went at the ditch and it has not come home since.',
        'I am not standing another watch on an empty stomach.',
        "Told him the post was rotten. Told him twice.",
      ],
      women: [
        'Levy took two off my row. Same work, fewer hands.',
        'I have mended that coat past mending.',
        'Whoever set that lintel set it drunk.',
        'The little one has the cough. Everyone has the cough.',
      ],
    },

    gather: {
      both: [
        'Queue moves when the priest says it moves.',
        'Price of iron again. Twice what it was.',
        'Nobody has seen the carter since Mudmonth.',
        'Stand here long enough and you hear everything twice.',
        'They opened the granary. A hand each.',
        'Word from the south, and none of it good.',
        'Somebody put a name on the stone last night.',
        'Barrel of salt, and he wants iron for it.',
        'That is the third stall gone dark this month.',
        'No, after you. I have all afternoon and no bread.',
        'Everybody here knows. Nobody here says.',
      ],
      men: ['Same well, longer rope.', 'I will wait. I have nowhere better.'],
      women: ['Weigh it in front of me this time.', 'That is not a full measure and you know it.'],
    },

    rest: {
      both: [
        'Sit while it is quiet. It will not be.',
        'Feet first. Everything else after.',
        'Ten minutes. That is all I am taking.',
        'Do not tell me the hour.',
        'Fire is warm. Fire is enough.',
        'I dreamt of a field with nothing in it.',
        'Nobody is looking. Nobody has looked in years.',
        'If the horn goes I am already up. Watch me.',
        'Sat down wrong. Now I live here.',
        'It is not resting. It is waiting, sitting.',
      ],
      men: ['Wake me for the horn and nothing else.'],
      women: ['I have been on my feet since the bell.'],
    },

    pray: {
      both: [
        'For the ones under the ash.',
        'Not for me. For the gate.',
        'Say it properly or do not say it.',
        'Whatever is listening — one more winter.',
        'I have nothing to give but the words.',
      ],
    },

    work: {
      both: [
        'Two more and I am done.',
        'It goes where it goes.',
        'Lift with me. On three.',
        'Blunt again. Everything is blunt.',
        'This will hold until it does not.',
      ],
      men: ['Mind the end of it!'],
      women: ['Slower. It will split.'],
    },

    /** Said with a full basket, on the way back. See the header. */
    haul: {
      both: [
        'Heavier going back than coming out.',
        'Yard is that way. I know where the yard is.',
        'Last load. I mean it this time.',
        'Half of this is water weight.',
        'Straps are cutting again.',
      ],
      men: ['Make a hole!'],
      women: ['Coming through — shift.'],
    },
  },
};

/**
 * What a *particular* people says, layered on top of `COMMON`.
 *
 * Deliberately thin. `RHYTHMS` in `villagers.ts` already gives each race a
 * different *shape* of day — the Vigil at the lamp, the Splitmoot arguing, the
 * Karrun indoors — and a handful of lines is enough to name that shape out
 * loud. Eleven full banks would be eleven banks to keep in voice, and the
 * lesson from `RHYTHMS` is that a table beats eleven behaviours.
 */
export const CHATTER: Record<string, ChatterBank> = {
  /** Men. Tired, and the baseline the others are departures from. */
  ashen: {
    moods: {
      talk: { both: ['We were somebody, before.', 'Debt outlives the man who took it.'] },
      rest: { both: ['One more winter. That is the whole plan.'] },
    },
  },

  /** Nine hundred oaths, and somebody is keeping every one of them. */
  vigil: {
    moods: {
      talk: { both: ['That is the fourth oath you have broken today.', 'Say the whole verse.'] },
      pray: {
        both: [
          'Ninth hour. As it was written.',
          'The lamp does not go out. I do not go out.',
          'I have kept it. I will keep it.',
        ],
      },
      rest: { both: ['Rest is also a duty. It is written.'] },
    },
  },

  /** They go back down. A dwarf off shift is a dwarf indoors. */
  karrun: {
    moods: {
      talk: { both: ['Surface. Wind in it. Never liked it.', 'That seam runs another mile, I swear it.'] },
      rest: { both: ['Door shut. Fire lit. Do not knock.', 'Ceiling too high in this one.'] },
      work: { both: ['Grain of the stone tells you. Listen to it.'] },
    },
  },

  /** The Choir is out among the groves, and it is never quite still. */
  verdant: {
    moods: {
      talk: { both: ['The east grove has gone quiet. That is not nothing.'] },
      work: { both: ['Ask before you cut. Then cut.'] },
      pray: { both: ['Root, branch, and the long dark under both.'] },
    },
  },

  /** Few, and formal. They gather, and they do not mill about. */
  silver: {
    moods: {
      talk: { both: ['We have seen this before. We will see it again.'] },
      gather: { both: ['The matter is tabled. It will keep.', 'Precedence, then business.'] },
    },
  },

  /** The Court keeps to the water's edge and does not explain itself. */
  drowned: {
    moods: {
      talk: { both: ['The tide came up wrong.', 'Do not answer it if it uses your name.'] },
      rest: { both: ['I can hear the water from here. I always can.'] },
    },
  },

  /** The Assize sits. Everything here happens at an appointed place. */
  crimson: {
    moods: {
      talk: { both: ['File it or forget it.', 'That is a matter for the sitting.'] },
      gather: { both: ['Third on the list. I have been third since dawn.'] },
    },
  },

  /** The moot is a permanent argument conducted at volume. */
  splitmoot: {
    moods: {
      talk: {
        both: [
          'NO — you listen —',
          'That was MY idea and you know it!',
          'Vote again! Vote AGAIN!',
          'Two of me disagree with you.',
        ],
      },
      gather: { both: ['Who called this?! WHO CALLED THIS?'] },
    },
  },

  /** Crews. Even off shift they are stood round somebody else's work. */
  cogwarren: {
    moods: {
      talk: { both: ['That is not how the gearing works.', 'It ran for nine hours. Then it did not.'] },
      gather: { both: ['Watch him do it wrong. It is instructive.'] },
      work: { both: ['Tolerance is off. I can hear it.'] },
    },
  },

  /**
   * The unburied do not rest, do not eat and have nothing to say. `RHYTHMS`
   * gives them a `talk` weight of zero; this covers the other four moods, and
   * the silence of a Litany street is the point of the people.
   */
  litany: { silent: true, moods: {} },

  /** No walls, no doors, no appointments. They walk, and they walk together. */
  moonbound: {
    moods: {
      talk: { both: ['Walk with me. Talk after.', 'Wind changed. We should be moving.'] },
      rest: { both: ['Ground is as good as a bed. Better.'] },
    },
  },
};

/**
 * Every line this mouth might say in this mood, common and racial together.
 *
 * Memoised, because the renderer asks per bubble per frame and the answer is a
 * pure function of two constants. This is a cache the rule in `COORDINATION.md`
 * permits without argument: it is not observable, because there is nothing here
 * for it to be observable *by* — the simulation cannot see this file, and two
 * calls with the same arguments return the same array for the life of the
 * process and for the life of any other process.
 *
 * Returns an empty array for a silent people, and the caller must handle that
 * rather than indexing into nothing.
 */
const resolved = new Map<string, readonly string[]>();

export function linesFor(raceId: string, mood: Mood, mouth: MouthId): readonly string[] {
  const key = `${raceId}|${mood}|${mouth}`;
  const hit = resolved.get(key);
  if (hit !== undefined) return hit;

  const bank = CHATTER[raceId];
  let out: readonly string[];
  if (bank?.silent) {
    out = [];
  } else {
    const pick = (m: MoodLines | undefined): readonly string[] =>
      m === undefined
        ? []
        : [...(m.both ?? []), ...(mouth === Mouth.Man ? (m.men ?? []) : (m.women ?? []))];
    out = [...pick(COMMON.moods[mood]), ...pick(bank?.moods[mood])];
  }

  resolved.set(key, out);
  return out;
}
